import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { projectToday } from '@d-orca/shared/client/activities';
import { setTaskStatus, TaskRescheduleConflictError, type ITaskSpan } from '@apis/schedule';
import { ChecklistIncompleteError } from '@apis/checklists';
import { committedActivitiesKey, scheduleKey, WEATHER_DAY_KEY } from '@constants/queryKeys';
import type { ISODate, ISchedulerData, ISchedulerTask, SiteId, TaskId } from '@interfaces/IScheduler';
import { useToast } from '@components/ui/Toasts/toastContext';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useCurrentActor } from '@hooks/useCurrentActor';
import { finishTiming } from '../lib/finishPlan';
import { checklistRefusedToast } from '../lib/checklistRefusedToast';
import { usePullPlan } from './usePullPlan';
import { useSchedulerActions } from './useSchedulerStore';
import { useTaskLifecycleActions } from './useTaskLifecycleActions';

// ---------------------------------------------------------------------------
// useTaskFinish — Mark done, asked on the day it is pressed
// (docs/specs/2026-09-22-done-on-the-day-amendment.md).
//
// On the planned end day there is nothing to ask and done is the ordinary
// immediate write. Before it or after it, the drawer asks (FinishTaskDialog),
// and an answer that moves dates goes out as ONE awaited write — status and
// spans together, version-checked — like a commit, not a queued slider tick:
// it can be refused as stale, and the plan it changed is re-read and rebased
// onto the store afterwards.
//
// The spans are computed against the server's copy (the schedule query cache),
// not the working copy: that is the copy the version check is against, and the
// rebase then lays any unsaved Gantt edits back on top.
// ---------------------------------------------------------------------------

/** The question the drawer is asking, and everything it is asked about. */
export interface IFinishQuestion {
  timing: 'early' | 'late';
  today: ISODate;
  scheduleNumber: string;
  /** The server's copy the answer is computed against, and its version. */
  base: ISchedulerData;
  baseVersion: string;
  /** The task as that copy has it. */
  task: ISchedulerTask;
}

export interface ITaskFinish {
  /** Mark done — straight through on the planned end day, otherwise ask. */
  requestDone: (task: ISchedulerTask) => void;
  question: IFinishQuestion | null;
  /** Answer the question: the spans to write, or `null` for "no date moves". */
  confirm: (question: IFinishQuestion, spans: ITaskSpan[] | null) => Promise<void>;
  dismiss: () => void;
  pending: boolean;
}

export interface IUseTaskFinishArgs {
  siteId: SiteId;
  taskId: TaskId;
  /** Loaded schedule number — absent means nothing to write against (tests). */
  scheduleNumber?: string;
}

/**
 * The question Mark done asks today, or `null` when it asks none: on the
 * planned end day, and whenever there is nothing to ask against — the site's
 * day not loaded yet, no server copy to move, or a task the server has never
 * seen (added in the Gantt, not saved yet). Done then behaves as it always did.
 */
function askFinish(
  today: ISODate | null,
  scheduleNumber: string | undefined,
  base: ISchedulerData | null | undefined,
  taskId: TaskId,
): IFinishQuestion | null {
  const task = base?.tasks.find((candidate) => candidate.id === taskId);
  if (!today || !scheduleNumber || !base?.version || !task) return null;
  const timing = finishTiming(task.end, today);
  return timing === 'onTime' ? null : { timing, today, scheduleNumber, base, baseVersion: base.version, task };
}

export function useTaskFinish({ siteId, taskId, scheduleNumber }: IUseTaskFinishArgs): ITaskFinish {
  const actor = useCurrentActor();
  const { t } = useTranslation();
  const pushToast = useToast();
  const qc = useQueryClient();
  const tz = useSelector(selectSiteTz);
  // A done the server refuses for an open checklist is reported, not retried.
  const onChecklistRefused = useCallback(() => pushToast(checklistRefusedToast(t)), [pushToast, t]);
  const { setStatus } = useTaskLifecycleActions(siteId, taskId, scheduleNumber, onChecklistRefused);
  const { taskStatusSet } = useSchedulerActions();
  const pullPlan = usePullPlan(siteId);
  const [question, setQuestion] = useState<IFinishQuestion | null>(null);
  const [pending, setPending] = useState(false);

  const requestDone = useCallback(
    (task: ISchedulerTask) => {
      const today = tz ? projectToday(tz) : null;
      const base = qc.getQueryData<ISchedulerData>(scheduleKey(siteId, scheduleNumber));
      const asked = askFinish(today, scheduleNumber, base, task.id);
      if (asked) setQuestion(asked);
      else setStatus('done');
    },
    [tz, scheduleNumber, qc, siteId, setStatus],
  );

  const confirm = useCallback(
    async (asked: IFinishQuestion, spans: ITaskSpan[] | null) => {
      if (spans === null) {
        setStatus('done');
        setQuestion(null);
        return;
      }
      setPending(true);
      try {
        await setTaskStatus(siteId, asked.scheduleNumber, taskId, 'done', { baseVersion: asked.baseVersion, tasks: spans });
        // The log entry the server derives, mirrored now; the rebase brings the
        // dates and the status (the overlay's log is never rebased).
        taskStatusSet({
          siteId,
          taskId,
          status: 'done',
          activityId: `act-${crypto.randomUUID()}`,
          at: new Date().toISOString(),
          by: actor.id,
        });
        setQuestion(null);
        await pullPlan(asked.scheduleNumber);
        void qc.invalidateQueries({ queryKey: committedActivitiesKey(siteId) });
        void qc.invalidateQueries({ queryKey: WEATHER_DAY_KEY });
      } catch (err) {
        if (err instanceof TaskRescheduleConflictError) {
          // Somebody moved the plan since the dialog read it: re-read, and ask
          // again against what is there now — or not at all, if the re-read
          // failed or the task now ends today.
          const fresh = await pullPlan(asked.scheduleNumber);
          setQuestion(askFinish(asked.today, asked.scheduleNumber, fresh, taskId));
          pushToast({ kind: 'error', title: t('scheduler.finish.conflict'), sub: t('scheduler.finish.conflictSub') });
        } else if (err instanceof ChecklistIncompleteError) {
          // Someone reopened a check: nothing was written. Re-read, and say why.
          setQuestion(null);
          await pullPlan(asked.scheduleNumber);
          pushToast(checklistRefusedToast(t));
        } else {
          pushToast({ kind: 'error', title: t('scheduler.finish.failed') });
        }
      } finally {
        setPending(false);
      }
    },
    [siteId, taskId, setStatus, taskStatusSet, pullPlan, qc, pushToast, t, actor.id],
  );

  const dismiss = useCallback(() => setQuestion(null), []);

  return { requestDone, question, confirm, dismiss, pending };
}
